import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useGoogleSignInMutation, useSignInMutation } from '@api/signIn';
import UserAuth from '@services/auth';
import { setLoginData } from '@pages/SignIn/slice';

import useToast from './useToast';
import {
  sendAmplitudeData,
  setAmplitudeUserProperties
} from '@utils/amplitude';
import {
  SIGN_IN_ERROR_INCORRECT_DETAILS,
  SIGN_IN_ERROR_NOT_VERIFIED,
  SIGN_IN_ERROR_NO_CLASSIFICATION
} from '@constants/eventNames';

const ACCOUNT_NOT_VERIFIED = 'Account not verified';
const VERIFIED_WITHOUT_PASSWORD_RESET = 'Verified without reset password';
const BUBBLE_API_FAILED = 'Bubble api failed';

const useSignIn = () => {
  const dispatch = useDispatch();
  const [signIn, { isLoading, isSuccess }] = useSignInMutation();
  const [googleSignIn] = useGoogleSignInMutation();
  const { pushErrorToast } = useToast();
  const [isInvalidCredentials, setIsInvalidCredentials] = useState(false);
  const [isIncorrectDetails, setIsIncorrectDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const userSignIn = async (
    email: string,
    password: string,
    auth_code: string
  ) => {
    setIsInvalidCredentials(false);
    try {
      const authData = auth_code
        ? await googleSignIn({ auth_code }).unwrap()
        : await signIn({ email, password }).unwrap();

      if (authData) {
        dispatch(setLoginData(authData));
        const { user, ...rest } = authData;

        UserAuth.setTokens(rest);
        UserAuth.setUserInfo(user);
        setAmplitudeUserProperties(user.id);

        UserAuth.setIsUserSignedIn(
          auth_code ? 'Google_sign_in' : 'email_password'
        );
      }
    } catch (err: any) {
      // Clear tokens on login failure
      UserAuth.clearTokens();

      if (err.status === 401) {
        setIsInvalidCredentials(true);
        if (err?.data?.detail === ACCOUNT_NOT_VERIFIED) {
          setErrorMessage(
            'It appears that you have not yet set a password. Please check your email for “Your account is Activated” email and follow the steps.'
          );
          sendAmplitudeData(SIGN_IN_ERROR_NOT_VERIFIED);
        } else if (err?.data?.detail === VERIFIED_WITHOUT_PASSWORD_RESET) {
          setErrorMessage('Please login through Google or reset a password.');
        } else if (err?.data?.detail === BUBBLE_API_FAILED) {
          setErrorMessage(
            'Looks like your e-mail ID is not registered. Register here first and then continue with using DataSquirrel.'
          );
        } else {
          setIsIncorrectDetails(true);
          setErrorMessage(
            'Incorrect details. Please provide correct e-mail ID and password.'
          );
          sendAmplitudeData(SIGN_IN_ERROR_INCORRECT_DETAILS);
        }
      } else {
        pushErrorToast(err?.data?.detail, { position: 'top-center' });
        sendAmplitudeData(SIGN_IN_ERROR_NO_CLASSIFICATION);
      }
    }
  };

  return {
    userSignIn,
    isSignInLoading: isLoading,
    isLoginSuccess: isSuccess,
    isInvalidCredentials,
    isIncorrectDetails,
    errorMessage,
    setIsInvalidCredentials
  };
};

export default useSignIn;
